import { useId, useState } from 'react';
import { Box, Menu, MenuItem } from '@mui/material';
import { styled } from '@mui/material/styles';

import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import { CITIES, switchCity, useCity, type CityKey } from './useCity';

/**
 * The city pill: a rounded outline holding the city's short name, drawn in
 * the serif the landing mockup sets "Boston" in. A tap lists the cities; the
 * ones not live yet are listed disabled, "Coming soon".
 *
 * `landing` is the mockup's size, over the photograph; `header` sits in the
 * app's top strip and headers, level with the lockup; `phone` in a phone's
 * header, level with the Saved button beside it (contract §15).
 */

const Pill = styled('button', { shouldForwardProp: (p) => p !== '$size' })<{
  $size: 'landing' | 'header' | 'phone';
}>(({ $size }) => ({
  appearance: 'none',
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  color: BRAND.parchment,
  border: '1.5px solid rgba(243,238,231,0.7)',
  borderRadius: 999,
  cursor: 'pointer',
  fontFamily: FONTS.condensed,
  fontWeight: 500,
  lineHeight: 1,
  whiteSpace: 'nowrap',
  transition: 'background-color 120ms ease, border-color 120ms ease',
  '&:hover': { background: 'rgba(243,238,231,0.12)', borderColor: BRAND.parchment },
  ...($size === 'landing'
    ? { height: 'clamp(40px, 3.1vw, 62px)', padding: '0 clamp(16px, 1.3vw, 26px)', fontSize: 'clamp(24px, 2vw, 40px)' }
    : $size === 'phone'
      ? { height: 28, padding: '0 11px', fontSize: 16 }
      : { height: 30, padding: '0 13px', fontSize: 19 }),
}));

interface Props {
  size: 'landing' | 'header' | 'phone';
  /** After a switch to another city: the app drops its distance filter here. */
  onSwitched?: (city: CityKey) => void;
}

export default function CityPill({ size, onSwitched }: Props) {
  const { city } = useCity();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const menuId = useId();
  const current = CITIES.find((c) => c.key === city) ?? CITIES[0];

  const choose = (key: CityKey) => {
    setAnchor(null);
    if (key === city) return;
    switchCity(key);
    onSwitched?.(key);
  };

  return (
    <>
      <Pill
        type="button"
        $size={size}
        aria-haspopup="menu"
        aria-expanded={anchor ? true : undefined}
        aria-controls={anchor ? menuId : undefined}
        aria-label={`City: ${current.label}. Change city`}
        onClick={(e) => setAnchor(e.currentTarget)}
      >
        {current.short}
      </Pill>
      <Menu
        id={menuId}
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              minWidth: 200,
              background: BRAND.mochaDeep,
              backgroundImage: 'none',
              color: BRAND.parchment,
              borderRadius: 2,
              border: '1px solid rgba(243,238,231,0.14)',
            },
          },
        }}
      >
        {CITIES.map((c) => (
          <MenuItem
            key={c.key}
            disabled={!c.enabled}
            selected={c.key === city}
            onClick={() => choose(c.key)}
            sx={{
              fontFamily: FONTS.body,
              fontSize: 14.5,
              gap: 1.5,
              justifyContent: 'space-between',
              '&.Mui-disabled': { opacity: 0.55 },
              '&.Mui-selected, &.Mui-selected:hover': { background: 'rgba(243,238,231,0.1)' },
            }}
          >
            <span style={{ fontWeight: c.key === city ? 700 : 400 }}>{c.label}</span>
            {!c.enabled && (
              <Box component="span" sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.04em', opacity: 0.8 }}>
                Coming soon
              </Box>
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
